/**
 * The integrations domain's FLAG MODULE (ruling 36 as amended 2026-08-29;
 * converted in flags fan-out batch 3).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/integrations/flags` publishes
 * both. `/contributions` carries nothing for flags — this domain has no such
 * door at all, and does not need one to declare a flag.
 *
 * ONE FLAG MODULE FOR A PACKAGE WITH SEVEN PRODUCT-AREA DOORS, and the choice is
 * worth stating because this package is shaped unlike its siblings: it publishes
 * `./ehr`, `./document-inbox`, `./letters`, `./connected-apps`, `./marketplace`,
 * `./translator` and `./problem-charting` and has NO `.` root barrel at all. The registry
 * (`PACKAGE_FLAG_GETTER_MODULES`) admits an array of getter modules precisely so
 * a domain large enough to split them by surface can, and this domain does not
 * split: two of the flags below gate two areas at once (the schedule pair is read
 * from both `./ehr`'s sidebar list and the appointment-list table; the Epic
 * markdown flag is read from the push button AND the Haiku consult-note), so a
 * per-area split would have to choose an owner for a flag both areas read, or
 * declare it twice. One module keeps ONE minting site, which is the property the
 * ruling is about.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard of their own (cache keys do), so this case and the
 * door-spanning one in `scripts/check-flag-getter-doors.test.ts` are the two
 * things standing between a key and a second spelling. This is the largest single-domain
 * conversion so far — nineteen keys, four of them near-twins (three
 * `capability-driven-push-*`, two `epic-*`, four `haiku-*`) — so the count matters
 * more here than anywhere it has mattered before.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/integrations/src`; `scripts/check-flag-getter-doors.test.ts` walks
 * this module's graph ACROSS package boundaries and refuses
 * `@heidi/design-system`, this package's own `/ui` zone and the app tree anywhere
 * in it, and separately proves the door imports in a bare Node process with no
 * DOM and no side effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/integrations/flags`, so its importers and
 * their `jest.mock` literals are untouched.
 *
 * TWO KEYS PUBLISHED AS CONSTANTS. `SCHEDULE_FILTER_OPTIONS_FLAG_KEY` and
 * `HAIKU_NOTE_PICKER_DIRECT_DISPATCH_FLAG_KEY` were app-barrel exports before the
 * move, and batch 1 set the rule that a conversion leaves the barrel's export
 * surface identical, so they cannot vanish. Each is DERIVED from its descriptor
 * here and re-exported through the barrel beside its getters; re-declaring either
 * literal in the barrel would be the second minting site this whole ruling exists
 * to refuse. The three module-PRIVATE key constants the barrel carried
 * (`EPIC_LAUNCH_MARKDOWN_NOTE_FLAG_KEY`,
 * `EPIC_PUSH_NOTE_BUTTON_LABEL_BY_OPTION_FLAG_KEY` and, for a getter that stays,
 * `ATHENA_JSON_SECTIONAL_NOTE_FLAG_KEY`) were never barrel exports, so the two
 * that move simply become descriptors with no barrel line.
 *
 * TWO OWNERSHIP CALLS THAT ARE NOT THE DEFAULT, named because a reader will
 * otherwise read them as mistakes:
 *
 *   - `op-scribe-disable-healthdirect-sidebar-collapse` is NOT in this domain's
 *     key namespace and its one consumer sits in the navigation tree
 *     (`useCollapseSidebarOnHealthDirectLaunch`), so neither of the two ownership
 *     tests fires. It is claimed here on the STORY: `op-scribe-` is the repo's
 *     generic kill-switch namespace rather than a domain one — its other members
 *     span session refetches, dictation and note rendering — so it carries no
 *     ownership signal at all, and what this gates is the HealthDirect launch,
 *     whose sibling flag (`release-integrations-healthdirect-launch-v2`) is
 *     unambiguously this domain's. Splitting one launch's two gates across the
 *     boundary would be worse than either home.
 *   - `op-integrations-disable-evidence-side-panel` gates the EVIDENCE nav entry,
 *     and two domains touch it. One flag has one owning domain: this one is
 *     integrations-namespaced and its condition is "inside an EHR Launch", which
 *     is this domain's surface, so integrations declares it and the Evidence nav
 *     item reads the barrel's re-export — a legal cross-root read, and the same
 *     arrangement ruling 36 (b) prescribes for a foreign domain's flag.
 *
 * WHAT DOES NOT BELONG HERE — TEN of this domain's owned app-barrel getters are
 * NOT below, and each is named rather than silently skipped. They fail on four
 * different things, and only one of them is PostHog. The count is ten and not
 * eleven because of one boundary case worth naming rather than arguing:
 * `useIsInboxClinicalTasksEnabled` ANDs this domain's inbox umbrella, so an
 * outside sweep of "integrations getters still app-side" reaches it — but its key
 * and its noun are `@heidi/tasks`, and batch 1 classified it in that lane. It is
 * counted there, and counting it here too would double-count it across lanes.
 *
 *   - A BESPOKE ENV VAR, which the door's `forceEnableAll` does not reproduce.
 *     `useIsNangoIntegrationsEnabled` reads `NEXT_PUBLIC_NANGO_FORCE_ENABLE` as
 *     well as the force-all knob, and its own docblock says why: "OpenFeature is
 *     not patched by FORCE_ENABLE_ALL alone for this Flagsmith key".
 *     `useDocumentInboxGate` reads `NEXT_PUBLIC_INBOX_SERVICE_URL` twice and
 *     resolves to `'off'` rather than `'on'` when it is missing. A package reads
 *     no env of its own, and these two reads are not force-all: they are product
 *     behaviour that would be silently dropped.
 *   - COMPOSING ONE OF THOSE. `useIsDocumentInboxEnabled` is a boolean wrapper
 *     over the tri-state gate, and `useIsInboxExtractionFeedbackEnabled` and
 *     `useIsInboxCustomAgentsEnabled` AND it. The composition inherits the
 *     mechanism, so all three stay with it. (A fourth,
 *     `useIsInboxClinicalTasksEnabled`, ANDs the same gate but is `@heidi/tasks`
 *     by noun and was already classified in batch 1.)
 *   - A RETURN SHAPE THE DOOR HAS NO READER FOR. `useDocumentInboxGate` returns
 *     `'on' | 'off' | 'unknown'` built from `useOpenFeatureClientStatus()`, which
 *     is a second disqualifier on top of its env reads.
 *   - PostHog. `useIsIntegrationAgentEnabled` resolves through
 *     `useFeatureFlagEnabled`, so moving it would change WHICH PROVIDER answers
 *     (`monorepo-wave-1-p3-feature-flags` Z4).
 *   - NO DESCRIPTOR CAN HOLD THEIR DEFAULT.
 *     `useAthenaJsonSectionalNoteFlagValue` and its non-React twin
 *     `getAthenaJsonSectionalNoteFlagValue` were STRING reads defaulting to
 *     `'off'`. The door publishes `useStringFlag` and `readStringFlag`, so the
 *     READ half is available — but `defineFeatureFlag`'s `defaultValue` is typed
 *     `boolean`, `isFeatureFlagContribution` refuses a non-boolean default, and
 *     `packages/runtime/src/shell/shell.test.ts` pins that refusal by name. With
 *     nowhere to DECLARE the pair, a string getter here would have to hardcode
 *     its key and default, which is the defect the ruling exists to refuse.
 *     RETIRED (AME-1002): all four Athena sectional STRING hooks were removed
 *     with the client-side classification they served — the editor version is
 *     now resolved by the backend and consumed via
 *     `src/hooks/editor-version/useResolvedEditorVersion` (ml-scribe #18956).
 *     The classification is preserved here because the same string-default
 *     blocker applies to any future string flag move.
 *
 * Every one of the ten carries its classification at its app-barrel site as well
 * as here.
 */
import {
  defineFeatureFlag,
  readFlag,
  useBooleanFlag,
} from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor. Two of the
// nineteen below look like typos and are not: `release-integrations-athena-multi-provider-schedule`
// predates the provider-agnostic rework of the schedule filters, and
// `release_integrations_enable_haiku_note_time_rendering` is UNDERSCORE-separated
// where every other key in this file is hyphenated. Both are what Flagsmith
// targets, so both are transcribed exactly.
//
// `builds` OMITTED ON ALL THIRTY-NINE, which takes the D6 default (`'cloud-only'`)
// and withholds them from the on-prem appliance. Measured, and a decision rather
// than a forgotten field: the EMR-integrations settings entry is
// `!isOnPrem`-gated in
// `src/components/layout/Navigation/settings/hooks/useSettingsList.tsx`, the
// Schedule (Upcoming) sidebar tab is `!isOnPrem`-gated in
// `src/components/layout/Navigation/sessions/side-bar.tsx`, and the EHR-launch
// surfaces these mostly gate (Epic, Haiku, Athena, HealthDirect — SMART on FHIR
// and vendor launches) are not served by the appliance at all.
//
// THE ONE THING THAT WOULD CHANGE THIS ANSWER, stated so the next reader does not
// have to re-derive it: the integrations settings GROUP *is* admitted on-prem,
// when `enableDocumentInbox`, `enableIntegrationAgent` or `enableClinicalCoding`
// is on. None of the thirty-nine keys below gates any of those three — the inbox
// umbrella and the integration agent both stay app-side (see the header) and the
// coding flag belongs to `@heidi/coding` — so the appliance loses nothing here.
// A future flag on an inbox or agent surface would need `builds: 'all'` and its
// own measured sentence. Contrast `@heidi/evidence`, converted in the same batch
// on the opposite finding: it declares `'all'` on all fifteen.
// ---------------------------------------------------------------------------

/** The "revert integration" confirmation dialog on the integrations settings page. */
export const revertIntegrationFlag = defineFeatureFlag({
  key: 'release-revert-integration',
  defaultValue: false,
  description: 'Revert-integration dialog on integrations settings',
});

/**
 * The reworked HealthDirect launch flow — per-launch guards plus
 * purge-all-onboarding-sessions-then-create, a stopgap while the backend cannot
 * handle duplicate onboarding sessions. OFF runs the legacy flow exactly as
 * shipped (`useOnboardingHealthDirectLegacy`).
 *
 * Callers must pair this with `useAreFeatureFlagsResolved` before acting — an
 * unresolved provider reads as the default `false`. That pairing is the CALLER's
 * and stays app-side, which is why this body was a plain read and could move.
 */
export const healthdirectLaunchV2Flag = defineFeatureFlag({
  key: 'release-integrations-healthdirect-launch-v2',
  defaultValue: false,
  description: 'Reworked HealthDirect launch flow',
});

/**
 * Kill switch for forcing the nav sidebar collapsed on every HealthDirect launch
 * (the HD iframe is space-constrained). Default OFF: the collapse-on-open
 * behaviour is active. Flip ON to disable it and restore the persisted
 * expand/collapse preference. Kill gate, not an experiment.
 *
 * Its key is in the generic `op-scribe-` namespace rather than this domain's —
 * see the header for why it is claimed here anyway.
 */
export const healthdirectSidebarCollapseKillFlag = defineFeatureFlag({
  key: 'op-scribe-disable-healthdirect-sidebar-collapse',
  defaultValue: false,
  description: 'Kill switch for collapse-sidebar-on-HealthDirect-launch',
});

/**
 * Capability-driven push-note button: the button's availability comes from the
 * integration's declared capabilities rather than a hardcoded provider list.
 */
export const capabilityDrivenPushNoteButtonFlag = defineFeatureFlag({
  key: 'release-integrations-enable-capability-driven-push-note-button',
  defaultValue: false,
  description: 'Capability-driven push-note button',
});

/**
 * Warn markdown-sectional users (Athena, AdvancedMD) before a sectional push
 * when note sections with content have no EHR section link, since the push
 * drops them.
 */
export const unlinkedSectionsPushWarningFlag = defineFeatureFlag({
  key: 'release-integrations-enable-unlinked-sections-push-warning',
  defaultValue: false,
  description: 'Pre-push warning for unlinked note sections',
});

/**
 * The Magentus arm of the same rollout, ramped separately from the general one
 * above because that vendor's capability payload landed later.
 */
export const capabilityDrivenPushNoteButtonMagentusFlag = defineFeatureFlag({
  key: 'release-integrations-enable-capability-driven-push-note-button-magentus',
  defaultValue: false,
  description: 'Capability-driven push-note button — Magentus arm',
});

/** The same capability-driven treatment for the push-DOCUMENT button. */
export const capabilityDrivenPushDocumentButtonFlag = defineFeatureFlag({
  key: 'release-integrations-enable-capability-driven-push-document-button',
  defaultValue: false,
  description: 'Capability-driven push-document button',
});

/**
 * When ON, hides the Evidence sidebar NAV ENTRY while the app is running inside
 * an EHR Launch (SMART on FHIR — Cerner, Epic, etc.). OFF keeps the entry visible
 * as before, even in an EHR Launch.
 *
 * It gates only the nav entry, not the Evidence surface: the panel can still be
 * opened by auto-open on transcription, floating Ask Heidi, clinical-task CTAs
 * and deep links. Full-surface suppression would be threaded through
 * `useEvidenceAccess`.
 */
export const evidenceSidePanelDisabledInEhrLaunchFlag = defineFeatureFlag({
  key: 'op-integrations-disable-evidence-side-panel',
  defaultValue: false,
  description: 'Hide the Evidence nav entry during an EHR Launch',
});

/** The post-visit Fullscript note handoff in scribe-fe-v2. */
export const fullscriptCopyOpenFlag = defineFeatureFlag({
  key: 'release-post-visit-fullscripts-copy-open',
  defaultValue: false,
  description: 'Post-visit Fullscript copy-and-open handoff',
});
export const FULLSCRIPT_COPY_OPEN_FLAG_KEY = fullscriptCopyOpenFlag.key;

/** The connected Fullscript onboarding and handoff rollout. */
export const fullscriptConnectedFlag = defineFeatureFlag({
  key: 'release-integrations-enable-fullscript-connected-handoff',
  defaultValue: false,
  description: 'Connected Fullscript onboarding and handoff',
});

/**
 * The SharePoint Library Ingest surface — the Library "Sharepoint"
 * scope filter pill, and on the backend the `/sharepoint/*` router and the
 * ingestion entry point. Without it the settings connect page is visible but
 * Library ingest stays gated.
 */
export const sharepointLibraryIngestFlag = defineFeatureFlag({
  key: 'release-integrations-enable-sharepoint-library-ingest',
  defaultValue: false,
  description: 'SharePoint Library ingest surface',
});

/** The v2 integration marketplace listing and its settings entry points. */
export const integrationMarketplaceV2Flag = defineFeatureFlag({
  key: 'release-integrations-marketplace-v2',
  defaultValue: false,
  description: 'Integration marketplace v2',
});

/**
 * ANZ-2490: hide marketplace discovery (Flagsmith `country` segments, not region).
 * ON hides discovery. Note: `forceEnableAll` / `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS`
 * also evaluates this as ON (same as every `useBooleanFlag` getter), so that
 * dev toggle hides the marketplace rather than showing it.
 */
export const hideIntegrationsMarketplaceFlag = defineFeatureFlag({
  key: 'release-hide-integrations-marketplace',
  defaultValue: false,
  description:
    'Hide Integrations Marketplace discovery for targeted accounts (force-enable-all also hides)',
});

/**
 * Provider-agnostic dept/group columns on the integration schedule
 * appointment list, driven by `appointment.display_facets` (`show_in_row`) from
 * GET schedule.
 */
export const scheduleFilterOptionsFlag = defineFeatureFlag({
  key: 'release-integrations-enable-schedule-filter-options',
  defaultValue: false,
  description: 'Provider-agnostic schedule filter columns',
});

/**
 * THE KEY, PUBLISHED — derived, never re-declared. It was an app-barrel export
 * before the move, so the name has to survive; minting the literal a second time
 * in the barrel is what ruling 36 refuses; the repo-wide door-spanning check in
 * `scripts/check-flag-getter-doors.test.ts` is what catches it.
 */
export const SCHEDULE_FILTER_OPTIONS_FLAG_KEY = scheduleFilterOptionsFlag.key;

/**
 * Rollout flag for provider group / department schedule filters. The key predates
 * the provider-agnostic rework, hence the "athena" in it — frozen, because
 * Flagsmith targets the flag by it.
 */
export const multiProviderScheduleFlag = defineFeatureFlag({
  key: 'release-integrations-athena-multi-provider-schedule',
  defaultValue: false,
  description: 'Provider group / department schedule filters',
});

/**
 * When ON, keeps notes as plain markdown for Epic EHR Launch (web) and Haiku —
 * skipping the markdown-to-sectional conversion `useJsonTemplateDefault`
 * normally enables for those surfaces — and forces "sync whole note" as the only
 * push option. When OFF, sectional generation and the dropdown are preserved.
 *
 * Read BOTH ways: the non-React reader below exists because the legacy
 * stream-response pipeline reads the JSON-template signal outside React.
 */
export const epicLaunchMarkdownNoteFlag = defineFeatureFlag({
  key: 'release-integrations-enable-epic-launch-markdown-note',
  defaultValue: false,
  description: 'Plain-markdown notes for Epic EHR Launch and Haiku',
});

/**
 * When ON, Epic Hyperspace tabs (legacy Epic FHIR auth) render a stored JSON
 * sectional consult note as a read-only flattened whole note instead of the
 * sectional block editor. Generation and Haiku behaviour are unchanged;
 * regeneration preserves the note-in-use format via the stream-response
 * conversion leg.
 */
export const hyperspaceWholeNoteViewFlag = defineFeatureFlag({
  key: 'release-integrations-enable-hyperspace-whole-note-view',
  defaultValue: false,
  description: 'Read-only whole-note view of JSON notes in Epic Hyperspace',
});

/**
 * When ON, the Epic note-push split button labels its primary action with the
 * currently selected push option ("Push sections to {Epic}" / "Push whole note to
 * {Epic}") instead of the static "Push to {Epic}". Read only inside
 * `PushEpicSdesComponent`, which already waits on `usePosthogFlagsReady` for the
 * sibling sectional flag, so the label never appears before the selected option
 * itself is stably resolved.
 */
export const epicPushNoteButtonLabelByOptionFlag = defineFeatureFlag({
  key: 'release-integrations-enable-epic-push-note-button-label-by-option',
  defaultValue: false,
  description: 'Epic push-note split button labelled by selected option',
});

/**
 * Shows the calendar event title (the Google Calendar invite `summary`) as the
 * primary label on the Schedule surfaces — the side-nav Schedule list and the
 * appointment-list table column. OFF falls back to the patient name, preserving
 * pre-rollout behaviour.
 */
export const calendarEventTitleFlag = defineFeatureFlag({
  key: 'release-integrations-enable-calendar-event-title',
  defaultValue: false,
  description: 'Calendar event title as the Schedule primary label',
});

/**
 * Lifts Haiku note-generation orchestration (polling, terminate, note-gen
 * kickoff) into a single stable session-scoped owner so it survives tab switches.
 * OFF is the legacy per-consumer instantiation. Kill switch / rollout gate, NOT
 * an experiment.
 */
export const haikuStableOrchestrationFlag = defineFeatureFlag({
  key: 'release-integrations-haiku-stable-orchestration',
  defaultValue: false,
  description: 'Session-scoped Haiku note-generation orchestration',
});

/** The Haiku consent popup shown when the recording settings are unknown. */
export const haikuConsentPopupOnUnknownSettingsFlag = defineFeatureFlag({
  key: 'release-integrations-haiku-consent-popup-on-unknown-settings',
  defaultValue: false,
  description: 'Haiku consent popup on unknown recording settings',
});

/**
 * After Haiku stop, start note writing directly instead of going through the
 * shared desktop "after recording" path — that path can open a template picker
 * Haiku cannot use and leave the UI stuck on "generating". Also unsticks sooner
 * when the backend never started a note job. Kill switch / rollout, not an
 * experiment.
 */
export const haikuNotePickerDirectDispatchFlag = defineFeatureFlag({
  key: 'release-integrations-haiku-note-picker-direct-dispatch',
  defaultValue: false,
  description: 'Direct note dispatch after a Haiku stop',
});

/**
 * THE SECOND PUBLISHED KEY, derived for the same reason as the first: it was an
 * app-barrel export and the barrel's surface stays identical through a
 * conversion.
 */
export const HAIKU_NOTE_PICKER_DIRECT_DISPATCH_FLAG_KEY =
  haikuNotePickerDirectDispatchFlag.key;

/**
 * The Haiku session-time rendering fix. ON renders
 * `created_time_local` in `SessionInfoToolbar` as a verbatim wall-clock time
 * (zone suffix stripped, no device-timezone conversion) so it matches the Epic
 * visit time; OFF parses it as a UTC instant and converts to the device timezone.
 * Kill switch / rollout gate, NOT an experiment.
 *
 * The key is UNDERSCORE-separated where every other key in this file is
 * hyphenated. That is what Flagsmith targets, so it is transcribed exactly rather
 * than tidied.
 */
export const haikuNoteTimeRenderingFlag = defineFeatureFlag({
  key: 'release_integrations_enable_haiku_note_time_rendering',
  defaultValue: false,
  description: 'Verbatim wall-clock rendering of the Haiku session time',
});

/**
 * ON keeps the Haiku native recording running through a screen lock or
 * background; a web view that iOS reloads starts IDLE and needs a manual Start.
 * OFF pauses the recording on `willBackground`. Kill switch / rollout gate, NOT
 * an experiment.
 */
export const haikuKeepRecordingInBackgroundFlag = defineFeatureFlag({
  key: 'release-integrations-haiku-keep-recording-in-background',
  defaultValue: false,
  description: 'Keep Haiku recording through screen lock and background',
});

/**
 * The Integrations button in the sidebar nav.
 *
 * Its key is `exp-`prefixed and its docblock calls it an experiment, but it
 * carries NO `useManualExposure` pairing app-side and gains none here: the
 * exposure invariant is about preserving a pairing that exists, and adding one
 * during a relocation would start emitting an experiment exposure that has never
 * been emitted. If this really is experiment-backed, the pairing is a separate
 * change with the `manual-exposure` skill's checklist behind it.
 */
export const integrationButtonInSidebarFlag = defineFeatureFlag({
  key: 'exp-integration-button-in-sidebar',
  defaultValue: false,
  description: 'Integrations button in the sidebar nav',
});

/**
 * Country-scoped EHR selection: the full-list EHR/EMR dropdowns fetch their
 * options from template-gallery's `integration-providers` endpoint (scoped to
 * a country) instead of enumerating the generated `EHRProvider` enum.
 */
export const countrySpecificEhrSelectionFlag = defineFeatureFlag({
  key: 'release-scribe-country-specific-ehr-selection',
  defaultValue: false,
  description: 'EHR dropdown options fetched country-scoped from template-gallery',
});

// ---------------------------------------------------------------------------
// The translator family: the nav gate, the evaluation pair, the
// echo-cancellation toggle, and sixteen per-language gates. The two translator
// STRING flags (`config-translator-release-test`, `release-translator-onboarding`)
// stay app-side in `src/features/flags/translator-flags.ts`; no descriptor can
// hold a string default yet, and `useTranslatorLanguageGates` stays with them
// because it composes the release-test read.
// ---------------------------------------------------------------------------

/** The Translator entry in the sidebar nav and the /translator routes. */
export const translatorNavFlag = defineFeatureFlag({
  key: 'release-translator-nav',
  defaultValue: false,
  description: 'Translator nav entry and routes',
});

/** The translator evaluation UI (per-turn scoring surface). */
export const translatorEvaluationUiFlag = defineFeatureFlag({
  key: 'release-translator-enable-evaluation-ui',
  defaultValue: false,
  description: 'Translator evaluation UI',
});

/** The Monash evaluation summary panel in the translator. */
export const translatorMonashEvaluationFlag = defineFeatureFlag({
  key: 'release-translator-enable-monash-evaluation-summary',
  defaultValue: false,
  description: 'Translator Monash evaluation summary',
});

/**
 * The session-level NUH post-session feedback form, opened from the same
 * session evaluation control as the Monash summary panel. Independent of the
 * evaluation-ui flag: the per-message thumb up/down and the detail form behind
 * it are unchanged either way. Monash raters keep the summary panel — where
 * this and the Monash flag are both on, the summary wins, so nobody loses
 * their export control (the surface hook picks, not this getter).
 */
export const translatorNuhSessionFeedbackFlag = defineFeatureFlag({
  key: 'release-translator-enable-nuh-session-feedback',
  defaultValue: false,
  description: 'Translator NUH post-session feedback form',
});

/** The user-facing echo-cancellation toggle in translator settings. */
export const translatorEchoCancellationToggleFlag = defineFeatureFlag({
  key: 'config-translator-echo-cancellation-toggle',
  defaultValue: false,
  description: 'Translator echo-cancellation toggle',
});

/**
 * Hands-free recording mode in the translator: the microphone stays open and
 * browser-side voice-activity detection (Silero, self-hosted assets) cuts the
 * audio into turns, as the old app's `useRecordingHandsFree` did. Ships OFF in
 * cloud until the V2 server-side VAD lands; the on-prem flag bundle turns it on
 * (speaker attribution there comes from heidi-interpreter-service `/stt`).
 */
export const translatorHandsFreeFlag = defineFeatureFlag({
  key: 'release-translator-hands-free',
  defaultValue: false,
  description: 'Translator hands-free recording mode (browser VAD)',
});

/**
 * The bulk audio-upload button in the translator recording bar. The key is the
 * OLD app's (`translator-release-bulk-upload`, read there via
 * `useBooleanFlagValue`). Adopted, not renamed: Flagsmith targets the flag
 * by it, so the V2 button inherits the old rollout's state exactly, and one
 * feature keeps one key across both apps.
 */
export const translatorBulkUploadFlag = defineFeatureFlag({
  key: 'translator-release-bulk-upload',
  defaultValue: false,
  description: 'Translator bulk audio upload',
});

/**
 * Bulk upload as a backend job (start it, then poll it) instead of the browser
 * diarize, transcribe, translate and persist pipeline. The key is the OLD
 * app's (`release-translator-backend-bulk-upload`), so one feature keeps one
 * key across both apps. Off: the browser pipeline. On: the job.
 */
export const translatorBackendBulkUploadFlag = defineFeatureFlag({
  key: 'release-translator-backend-bulk-upload',
  defaultValue: false,
  description: 'Translator bulk upload as a backend job',
});

/**
 * The translation-quality picker (FAST/BEST model tiers) in translator
 * settings. The key is the OLD app's (`release-translator-enable-translation-
 * model-picker`, read there via `useBooleanFlagValue`). Adopted, not renamed:
 * the old picker shipped behind it defaulted OFF, so the V2 picker inherits
 * the same rollout state and one feature keeps one key across both apps.
 */
export const translatorTranslationModelPickerFlag = defineFeatureFlag({
  key: 'release-translator-enable-translation-model-picker',
  defaultValue: false,
  description: 'Translator translation-quality (model tier) picker',
});

/**
 * Progressive TTS playback: message audio plays from ml-scribe's
 * `tts:stream` as it is synthesized instead of after `tts:synthesize` has
 * stored the whole clip. Off keeps the `tts:synthesize` path unchanged.
 *
 * D6 default build profile (`'cloud-only'`) on purpose: the on-prem
 * interpreter service cannot stream, so the appliance has nothing to gate.
 */
export const translatorTtsStreamingFlag = defineFeatureFlag({
  key: 'release-translator-enable-tts-streaming',
  defaultValue: false,
  description: 'Translator progressive TTS playback',
});

/** Thai in the translator language pickers. */
export const translatorThaiFlag = defineFeatureFlag({
  key: 'release-translator-enable-thai',
  defaultValue: false,
  description: 'Translator language gate: Thai',
});

/** Hokkien in the translator language pickers. */
export const translatorHokkienFlag = defineFeatureFlag({
  key: 'release-translator-enable-hokkien',
  defaultValue: false,
  description: 'Translator language gate: Hokkien',
});

/** Arabic in the translator language pickers. */
export const translatorArabicFlag = defineFeatureFlag({
  key: 'release-translator-enable-arabic',
  defaultValue: false,
  description: 'Translator language gate: Arabic',
});

/** Italian in the translator language pickers. */
export const translatorItalianFlag = defineFeatureFlag({
  key: 'release-translator-enable-italian',
  defaultValue: false,
  description: 'Translator language gate: Italian',
});

/** Korean in the translator language pickers. */
export const translatorKoreanFlag = defineFeatureFlag({
  key: 'release-translator-enable-korean',
  defaultValue: false,
  description: 'Translator language gate: Korean',
});

/** French in the translator language pickers. */
export const translatorFrenchFlag = defineFeatureFlag({
  key: 'release-translator-enable-french',
  defaultValue: false,
  description: 'Translator language gate: French',
});

/** German in the translator language pickers. */
export const translatorGermanFlag = defineFeatureFlag({
  key: 'release-translator-enable-german',
  defaultValue: false,
  description: 'Translator language gate: German',
});

/** Indonesian in the translator language pickers. */
export const translatorIndonesianFlag = defineFeatureFlag({
  key: 'release-translator-enable-indonesian',
  defaultValue: false,
  description: 'Translator language gate: Indonesian',
});

/** Spanish in the translator language pickers. */
export const translatorSpanishFlag = defineFeatureFlag({
  key: 'release-translator-enable-spanish',
  defaultValue: false,
  description: 'Translator language gate: Spanish',
});

/** Turkish in the translator language pickers. */
export const translatorTurkishFlag = defineFeatureFlag({
  key: 'release-translator-enable-turkish',
  defaultValue: false,
  description: 'Translator language gate: Turkish',
});

/** Portuguese in the translator language pickers. */
export const translatorPortugueseFlag = defineFeatureFlag({
  key: 'release-translator-enable-portuguese',
  defaultValue: false,
  description: 'Translator language gate: Portuguese',
});

/** Polish in the translator language pickers. */
export const translatorPolishFlag = defineFeatureFlag({
  key: 'release-translator-enable-polish',
  defaultValue: false,
  description: 'Translator language gate: Polish',
});

/** Ukrainian in the translator language pickers. */
export const translatorUkrainianFlag = defineFeatureFlag({
  key: 'release-translator-enable-ukrainian',
  defaultValue: false,
  description: 'Translator language gate: Ukrainian',
});

/** Romanian in the translator language pickers. */
export const translatorRomanianFlag = defineFeatureFlag({
  key: 'release-translator-enable-romanian',
  defaultValue: false,
  description: 'Translator language gate: Romanian',
});

/** Tagalog in the translator language pickers. */
export const translatorTagalogFlag = defineFeatureFlag({
  key: 'release-translator-enable-tagalog',
  defaultValue: false,
  description: 'Translator language gate: Tagalog',
});

/** Russian in the translator language pickers. */
export const translatorRussianFlag = defineFeatureFlag({
  key: 'release-translator-enable-russian',
  defaultValue: false,
  description: 'Translator language gate: Russian',
});

/**
 * The session problem set rendered as its OWN surface below the note, read
 * from the `problem-set` read model, REPLACING the coding overlay that paints
 * pills over problem text inside the editor. Gates the `./problem-charting`
 * door's panel; moved here with that door from `packages/coding/src/flags.ts`,
 * key unchanged. One key moves both sides: the note tab stops feeding the
 * overlay when this is on, and the backend (ml-scribe, same key) stops
 * rewriting the note's A&P into the numbered form the overlay anchored to.
 * Off by default. While the generation template still hands the LLM the
 * extracted problem list, a PBC note lists its problems in the A&P text as
 * well as in the panel; that overlap clears when the backend drops the
 * template skeleton.
 *
 * D6 default build profile (`'cloud-only'`) on purpose: problem-based charting
 * is an Athena integration surface with a cloud-only coding pipeline behind
 * it, so the on-prem appliance has nothing for this flag to gate.
 */
export const problemSetPanelFlag = defineFeatureFlag({
  key: 'release-coding-problem-set-panel',
  defaultValue: false,
  description: 'Problem set rendered as its own panel below the note',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// ONE DISCLOSED BEHAVIOURAL DELTA, common to all twenty-seven and a gain rather
// than a risk: `useBooleanFlag` and `readFlag` honour the injected
// `forceEnableAll` override, which the `useBooleanFlagValue` /
// `getBooleanValue` calls they replace did not. That is the choke point the door
// exists for (design D8) and the knob is dev-only. NO forceAll COLLAPSE to
// disclose: not one of the twenty-five app-side bodies carried a hand-rolled
// `process.env` branch. The four getters that DID carry one all read a bespoke
// env var beside it and stay app-side for that reason — see the header.
// ---------------------------------------------------------------------------

/** The revert-integration dialog on integrations settings. */
export const useIsRevertIntegrationEnabled = (): boolean =>
  useBooleanFlag(revertIntegrationFlag.key, revertIntegrationFlag.defaultValue);

/** The reworked HealthDirect launch flow. */
export const useIsHealthdirectLaunchV2Enabled = (): boolean =>
  useBooleanFlag(healthdirectLaunchV2Flag.key, healthdirectLaunchV2Flag.defaultValue);

/** Kill switch for collapse-sidebar-on-HealthDirect-launch. */
export const useIsHealthdirectSidebarCollapseKilled = (): boolean =>
  useBooleanFlag(
    healthdirectSidebarCollapseKillFlag.key,
    healthdirectSidebarCollapseKillFlag.defaultValue
  );

/** The capability-driven push-note button. */
export const useIsCapabilityDrivenPushNoteButtonEnabled = (): boolean =>
  useBooleanFlag(
    capabilityDrivenPushNoteButtonFlag.key,
    capabilityDrivenPushNoteButtonFlag.defaultValue
  );

/** The pre-push warning for unlinked note sections. */
export const useIsUnlinkedSectionsPushWarningEnabled = (): boolean =>
  useBooleanFlag(
    unlinkedSectionsPushWarningFlag.key,
    unlinkedSectionsPushWarningFlag.defaultValue
  );

/** The Magentus arm of the capability-driven push-note button. */
export const useIsCapabilityDrivenPushNoteButtonMagentusEnabled = (): boolean =>
  useBooleanFlag(
    capabilityDrivenPushNoteButtonMagentusFlag.key,
    capabilityDrivenPushNoteButtonMagentusFlag.defaultValue
  );

/** The capability-driven push-document button. */
export const useIsCapabilityDrivenPushDocumentButtonEnabled = (): boolean =>
  useBooleanFlag(
    capabilityDrivenPushDocumentButtonFlag.key,
    capabilityDrivenPushDocumentButtonFlag.defaultValue
  );

/** Hide the Evidence nav entry during an EHR Launch. */
export const useIsEvidenceSidePanelDisabledInEhrLaunch = (): boolean =>
  useBooleanFlag(
    evidenceSidePanelDisabledInEhrLaunchFlag.key,
    evidenceSidePanelDisabledInEhrLaunchFlag.defaultValue
  );

/** The post-visit Fullscript note handoff in scribe-fe-v2. */
export const useIsFullscriptCopyOpenEnabled = (): boolean =>
  useBooleanFlag(fullscriptCopyOpenFlag.key, fullscriptCopyOpenFlag.defaultValue);

/** The connected Fullscript onboarding and handoff rollout. */
export const useIsFullscriptConnectedEnabled = (): boolean =>
  useBooleanFlag(fullscriptConnectedFlag.key, fullscriptConnectedFlag.defaultValue);

/** The SharePoint Library ingest surface. */
export const useIsSharepointLibraryIngestEnabled = (): boolean =>
  useBooleanFlag(
    sharepointLibraryIngestFlag.key,
    sharepointLibraryIngestFlag.defaultValue
  );

/** The v2 integration marketplace. */
export const useIsIntegrationMarketplaceV2Enabled = (): boolean =>
  useBooleanFlag(
    integrationMarketplaceV2Flag.key,
    integrationMarketplaceV2Flag.defaultValue
  );

/**
 * Whether marketplace discovery should be hidden for the current identity.
 * Pair with the app's identity-aware gate — do not use this boolean alone to
 * paint or redirect (pre-identify reads are the default OFF).
 */
export const useIsIntegrationsMarketplaceHidden = (): boolean =>
  useBooleanFlag(
    hideIntegrationsMarketplaceFlag.key,
    hideIntegrationsMarketplaceFlag.defaultValue
  );

/** Provider-agnostic schedule filter columns. */
export const useIsScheduleFilterOptionsEnabled = (): boolean =>
  useBooleanFlag(scheduleFilterOptionsFlag.key, scheduleFilterOptionsFlag.defaultValue);

/** Provider group / department schedule filters. */
export const useIsMultiProviderScheduleEnabled = (): boolean =>
  useBooleanFlag(multiProviderScheduleFlag.key, multiProviderScheduleFlag.defaultValue);

/** Plain-markdown notes for Epic EHR Launch and Haiku. */
export const useIsEpicLaunchMarkdownNoteEnabled = (): boolean =>
  useBooleanFlag(epicLaunchMarkdownNoteFlag.key, epicLaunchMarkdownNoteFlag.defaultValue);

/**
 * The same flag for non-React paths — the legacy stream-response pipeline reads
 * the JSON-template signal outside React.
 *
 * ONE DISCLOSED DELTA beyond the forceAll note above, and it is the one
 * `packages/dictate/src/flags.ts` records for `isCursorAwarenessEnabled`: the
 * app-side body built its own
 * `OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(…)`, which bypassed
 * the `forceEnableAll` override; `readFlag` honours it. Behaviour is otherwise
 * identical — `readFlag` resolves the same client on the same default domain, and
 * it removes a second place for the domain constant to be got wrong.
 */
export const isEpicLaunchMarkdownNoteEnabled = (): boolean =>
  readFlag(epicLaunchMarkdownNoteFlag.key, epicLaunchMarkdownNoteFlag.defaultValue);

/** Read-only whole-note view of JSON notes in Epic Hyperspace. */
export const useIsHyperspaceWholeNoteViewEnabled = (): boolean =>
  useBooleanFlag(
    hyperspaceWholeNoteViewFlag.key,
    hyperspaceWholeNoteViewFlag.defaultValue
  );

/** The same flag for non-React paths (the stream-response regen arming). */
export const isHyperspaceWholeNoteViewEnabled = (): boolean =>
  readFlag(hyperspaceWholeNoteViewFlag.key, hyperspaceWholeNoteViewFlag.defaultValue);

/** The Epic push-note split button labelled by the selected option. */
export const useIsEpicPushNoteButtonLabelByOptionEnabled = (): boolean =>
  useBooleanFlag(
    epicPushNoteButtonLabelByOptionFlag.key,
    epicPushNoteButtonLabelByOptionFlag.defaultValue
  );

/** The calendar event title as the Schedule primary label. */
export const useIsCalendarEventTitleEnabled = (): boolean =>
  useBooleanFlag(calendarEventTitleFlag.key, calendarEventTitleFlag.defaultValue);

/** Session-scoped Haiku note-generation orchestration. */
export const useIsHaikuStableOrchestrationEnabled = (): boolean =>
  useBooleanFlag(
    haikuStableOrchestrationFlag.key,
    haikuStableOrchestrationFlag.defaultValue
  );

/** The Haiku consent popup on unknown recording settings. */
export const useIsHaikuConsentPopupOnUnknownSettingsEnabled = (): boolean =>
  useBooleanFlag(
    haikuConsentPopupOnUnknownSettingsFlag.key,
    haikuConsentPopupOnUnknownSettingsFlag.defaultValue
  );

/**
 * Direct note dispatch after a Haiku stop, for non-React paths — `scribeRecordModel`
 * reads it outside React, and its SCREAMING_SNAKE name is the spelling that
 * model imports. Same disclosed delta as `isEpicLaunchMarkdownNoteEnabled` above.
 */
export const IS_HAIKU_NOTE_PICKER_DIRECT_DISPATCH_ENABLED = (): boolean =>
  readFlag(
    haikuNotePickerDirectDispatchFlag.key,
    haikuNotePickerDirectDispatchFlag.defaultValue
  );

/** Direct note dispatch after a Haiku stop, as a hook. */
export const useIsHaikuNotePickerDirectDispatchEnabled = (): boolean =>
  useBooleanFlag(
    haikuNotePickerDirectDispatchFlag.key,
    haikuNotePickerDirectDispatchFlag.defaultValue
  );

/** Verbatim wall-clock rendering of the Haiku session time. */
export const useIsHaikuNoteTimeRenderingEnabled = (): boolean =>
  useBooleanFlag(haikuNoteTimeRenderingFlag.key, haikuNoteTimeRenderingFlag.defaultValue);

/** Keep Haiku recording through screen lock and background. */
export const useIsHaikuKeepRecordingInBackgroundEnabled = (): boolean =>
  useBooleanFlag(
    haikuKeepRecordingInBackgroundFlag.key,
    haikuKeepRecordingInBackgroundFlag.defaultValue
  );

/** The Integrations button in the sidebar nav. */
export const useIsIntegrationButtonInSidebarEnabled = (): boolean =>
  useBooleanFlag(
    integrationButtonInSidebarFlag.key,
    integrationButtonInSidebarFlag.defaultValue
  );

/** Country-scoped EHR dropdown options from template-gallery. */
export const useIsCountrySpecificEhrSelectionEnabled = (): boolean =>
  useBooleanFlag(
    countrySpecificEhrSelectionFlag.key,
    countrySpecificEhrSelectionFlag.defaultValue
  );

/** The problem-set panel gate; read from `@/features/flags` by the note tab. */
export const useIsProblemSetPanelEnabled = (): boolean =>
  useBooleanFlag(problemSetPanelFlag.key, problemSetPanelFlag.defaultValue);

/** The Translator nav entry and routes. */
export const useTranslatorNavEnabled = (): boolean =>
  useBooleanFlag(translatorNavFlag.key, translatorNavFlag.defaultValue);

/** The translator evaluation UI. */
export const useTranslatorEvaluationUiEnabled = (): boolean =>
  useBooleanFlag(translatorEvaluationUiFlag.key, translatorEvaluationUiFlag.defaultValue);

/**
 * The Monash evaluation summary panel. The panel gates on this AND the
 * evaluation-ui flag together, while TranslatorFeedbackPanel reads this flag
 * alone to pick the monash_rubric variant, so the dependency is NOT composed
 * into this getter.
 */
export const useTranslatorMonashEvaluationEnabled = (): boolean =>
  useBooleanFlag(
    translatorMonashEvaluationFlag.key,
    translatorMonashEvaluationFlag.defaultValue
  );

/** The session-level NUH post-session feedback form. */
export const useTranslatorNuhSessionFeedbackEnabled = (): boolean =>
  useBooleanFlag(
    translatorNuhSessionFeedbackFlag.key,
    translatorNuhSessionFeedbackFlag.defaultValue
  );

export type TranslatorSessionEvaluationSurface = 'monash_summary' | 'nuh_feedback';

/**
 * Which form the session evaluation control opens, or null when the user gets
 * no control at all. The two cohorts' flags are set independently in
 * Flagsmith, so this is the one place that resolves them landing on the same
 * user: Monash raters keep the summary panel (and its export) — a rater
 * mid-study losing it is the more damaging surprise. Ported from the legacy
 * app's lib/flags.ts useSessionEvaluationSurface, including the evaluation-ui
 * conjunct: the summary panel gates on evaluation-ui AND monash together.
 */
export const useTranslatorSessionEvaluationSurface =
  (): TranslatorSessionEvaluationSurface | null => {
    const isEvaluationUiEnabled = useTranslatorEvaluationUiEnabled();
    const isMonashSummaryEnabled = useTranslatorMonashEvaluationEnabled();
    const isNuhSessionFeedbackEnabled = useTranslatorNuhSessionFeedbackEnabled();

    if (isEvaluationUiEnabled && isMonashSummaryEnabled) return 'monash_summary';
    if (isNuhSessionFeedbackEnabled) return 'nuh_feedback';
    return null;
  };

/** The echo-cancellation toggle in translator settings. */
export const useTranslatorEchoCancellationToggleEnabled = (): boolean =>
  useBooleanFlag(
    translatorEchoCancellationToggleFlag.key,
    translatorEchoCancellationToggleFlag.defaultValue
  );

/** Hands-free recording mode (browser VAD) in the translator. */
export const useTranslatorHandsFreeEnabled = (): boolean =>
  useBooleanFlag(translatorHandsFreeFlag.key, translatorHandsFreeFlag.defaultValue);

/** The bulk audio-upload button in the translator recording bar. */
export const useTranslatorBulkUploadEnabled = (): boolean =>
  useBooleanFlag(translatorBulkUploadFlag.key, translatorBulkUploadFlag.defaultValue);

/** Bulk upload as a backend job rather than the browser pipeline. */
export const useTranslatorBackendBulkUploadEnabled = (): boolean =>
  useBooleanFlag(
    translatorBackendBulkUploadFlag.key,
    translatorBackendBulkUploadFlag.defaultValue
  );

/** The translation-quality picker in translator settings. */
export const useTranslatorTranslationModelPickerEnabled = (): boolean =>
  useBooleanFlag(
    translatorTranslationModelPickerFlag.key,
    translatorTranslationModelPickerFlag.defaultValue
  );

export const useTranslatorTtsStreamingEnabled = (): boolean =>
  useBooleanFlag(translatorTtsStreamingFlag.key, translatorTtsStreamingFlag.defaultValue);

type TranslatorGatedLanguageCode =
  | 'th'
  | 'nan'
  | 'ar'
  | 'it'
  | 'ko'
  | 'fr'
  | 'de'
  | 'id'
  | 'es'
  | 'tr'
  | 'pt'
  | 'pl'
  | 'uk'
  | 'ro'
  | 'tl'
  | 'ru';

/**
 * The sixteen gated translator languages as a code → enabled map. The app-side
 * `useTranslatorLanguageGates` composes this with the release-test string flag,
 * so that composite stays app-side.
 */
export const useTranslatorGatedLanguages = (): Record<
  TranslatorGatedLanguageCode,
  boolean
> => ({
  th: useBooleanFlag(translatorThaiFlag.key, translatorThaiFlag.defaultValue),
  nan: useBooleanFlag(translatorHokkienFlag.key, translatorHokkienFlag.defaultValue),
  ar: useBooleanFlag(translatorArabicFlag.key, translatorArabicFlag.defaultValue),
  it: useBooleanFlag(translatorItalianFlag.key, translatorItalianFlag.defaultValue),
  ko: useBooleanFlag(translatorKoreanFlag.key, translatorKoreanFlag.defaultValue),
  fr: useBooleanFlag(translatorFrenchFlag.key, translatorFrenchFlag.defaultValue),
  de: useBooleanFlag(translatorGermanFlag.key, translatorGermanFlag.defaultValue),
  id: useBooleanFlag(translatorIndonesianFlag.key, translatorIndonesianFlag.defaultValue),
  es: useBooleanFlag(translatorSpanishFlag.key, translatorSpanishFlag.defaultValue),
  tr: useBooleanFlag(translatorTurkishFlag.key, translatorTurkishFlag.defaultValue),
  pt: useBooleanFlag(translatorPortugueseFlag.key, translatorPortugueseFlag.defaultValue),
  pl: useBooleanFlag(translatorPolishFlag.key, translatorPolishFlag.defaultValue),
  uk: useBooleanFlag(translatorUkrainianFlag.key, translatorUkrainianFlag.defaultValue),
  ro: useBooleanFlag(translatorRomanianFlag.key, translatorRomanianFlag.defaultValue),
  tl: useBooleanFlag(translatorTagalogFlag.key, translatorTagalogFlag.defaultValue),
  ru: useBooleanFlag(translatorRussianFlag.key, translatorRussianFlag.defaultValue),
});
